import {
  Button,
  CircularProgress,
  Grid,
  Typography,
  styled,
} from '@mui/material';
import { useGoogleLogin } from '@react-oauth/google';
import { AppleIcon, GoogleIcon } from 'assets/icons';
import { RollLogoImage } from 'assets/images';
import { useTranslation } from 'react-i18next';
import { AppleResponse } from 'types/apple-respone.type';
import { loginWithApple, loginWithGoogle } from '../store/auth.actions';
import { useDispatch, useSelector } from 'react-redux';
import { ACCESS_TOKEN, SESSION } from 'constants/local-storage';
import { useNavigate } from 'react-router';
import { sessionPendingSelector } from '../store/auth.selectors';
import { firebaseSignIn } from 'firebase';
import { UserSession } from '../types/user-session.type';
import AppleLogin from 'react-apple-login'; 
import { colors } from 'constants/colors';

const SignInButton = styled(Button)({
  margin: '7px 0',
  width: '100%',
});

const SectionGrid = styled(Grid)<{
  ownerState: { isPublic: boolean };
  component: string;
}>(({ theme, ownerState }) =>
  ownerState.isPublic
    ? {
        position: 'absolute',
        alignItems: 'center',
        justifyContent: 'center',
        flex: '1 1',
        zIndex: 1,
        background: '#272727',
        borderRadius: '12px 12px 0 0',
        padding: '40px 0 10px',

        [theme.breakpoints.up('md')]: {
          top: 'none',
          position: 'relative',
          borderRadius: '15px',
          padding: '30px',
        },
      }
    : {
        position: 'absolute',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '1 1',
        zIndex: 1,
        bottom: 0,
        top: 0,

        [theme.breakpoints.up('md')]: {
          top: 'none',
          position: 'relative',
          justifyContent: 'center',
        },
      }
);

interface AuthFormProps {
  navigateRoute: string;
  isPublic?: boolean;
}

const AuthForm = ({ navigateRoute, isPublic = false }: AuthFormProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const signInPending = useSelector(sessionPendingSelector);

  const loginWithGeneratedResponse = async () => {
    const response = JSON.parse(
      process.env.REACT_APP_GENERATED_AUTH_RESPONSE as string
    ) as UserSession;
   await firebaseSignIn(response.fbToken);
    localStorage.setItem(ACCESS_TOKEN, response.token);
    localStorage.setItem(SESSION, JSON.stringify(response));
    window.location.reload()
  }; 

  const loginGoogle = useGoogleLogin({
    onSuccess: async ({ access_token }) => {
      const { error, payload } = await dispatch<any>(
        loginWithGoogle({ access_token, refresh_token: 'NoRefreshToken' })
      );

      if (error) {
        return;
      }

      localStorage.setItem(ACCESS_TOKEN, payload.token);
      localStorage.setItem(SESSION, JSON.stringify(payload));

      navigate(navigateRoute);
    },
    prompt: 'consent',
  });

  const loginApple = async (response: AppleResponse) => {
    if (response.error) {
      return;
    }

    const { error, payload } = await dispatch<any>(
      loginWithApple(response.authorization)
    );

    if (error) {
      return;
    }

    localStorage.setItem(ACCESS_TOKEN, payload.token);
    localStorage.setItem(SESSION, JSON.stringify(payload));

    navigate(navigateRoute);
  };

  const handleGoogleLogin = () =>
    process.env.REACT_APP_GENERATED_AUTH_RESPONSE
      ? loginWithGeneratedResponse()
      : loginGoogle();

  return (
    <SectionGrid
      ownerState={{ isPublic }}
      component="section"
      item
      container
      direction="column"
      md={6}
    >
      <Grid
        sx={{
          transform: { xs: isPublic ? 'none' : 'translateY(300%)', md: 'none' },
        }}
      >
        <RollLogoImage />
      </Grid>

      <br />
      <Grid paddingBottom="30px" sx={{ background: colors.default.background }}>
        <Grid
          sx={{
            justifyContent: 'flex-start',
            width: '100%',
            padding: '0 35px',
          }}
        >
          <Typography
            variant="h6"
            component="h6"
            color="inherit"
            textTransform="uppercase"
            textAlign="left"
            sx={{ color: colors.default.darkWhite }}
          >
            {t('titles.log-in-create')}
          </Typography>
        </Grid>

        <Grid item container padding="16px 16px">
          {!signInPending && (
            <AppleLogin
              clientId={process.env.REACT_APP_APPLE_CLIENT_ID as string}
              redirectURI={process.env.REACT_APP_REDIRECT_URL as string}
              usePopup={true}
              callback={(response) => loginApple(response)}
              scope="email name"
              responseMode="query"
              render={(renderProps) => (
                <SignInButton
                  variant="social-auth"
                  color="white"
                  onClick={renderProps.onClick}
                  startIcon={<AppleIcon />}
                >
                  {t('buttons.sign-in-apple')}
                </SignInButton>
              )}
            />
          )}
          <SignInButton
            variant="social-auth"
            color="white"
            onClick={handleGoogleLogin}
            startIcon={signInPending ? <></> : <GoogleIcon />}
          >
            {signInPending ? (
              <CircularProgress size={24} />
            ) : (
              t('buttons.sign-in-google')
            )}
          </SignInButton>

          <Typography
            variant="body2"
            component="p"
            sx={{
              color: colors.default.thinWhite,
              textAlign: 'center',
              marginTop: '50px',
              fontSize: '12px',
              width: '100%',
            }}
          >
            {t('texts.by-clicking')}
            <Typography
              variant="body2"
              component="a"
              href={process.env.REACT_APP_TERMS_URL}
              sx={{
                fontSize: 'inherit',
                color: colors.default.info,
                textDecoration: 'none',
              }}
            >
              &nbsp;{t('subtitles.terms-of-service')}&nbsp;
            </Typography>
            {t('texts.acknowledge-our')}
            <Typography
              variant="body2"
              component="a"
              href={process.env.REACT_APP_PRIVACY_URL}
              sx={{
                fontSize: 'inherit',
                color: colors.default.info,
                textDecoration: 'none',
              }}
            >
              &nbsp;{t('subtitles.privacy-policy')}
            </Typography>
            .
          </Typography>
        </Grid>
      </Grid>
    </SectionGrid>
  );
};

export default AuthForm;
